import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { toastSuccess } from '@components/Toast';

import CollectionTags from '@features/collection/components/CollectionTags';
import { generateFilterParams } from '@features/filter/static/filterUtil';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import ApiClient from '@lib/apiClient';

import { isPageReachBottom } from '@utils/Util';

const SuggestedCollection = ({ setTotalCount }) => {
	const [collections, setCollections] = useState([]);
	const [activeTab, setActiveTab] = useState('PENDING');
	const filterData = useSelector((store) => store.filterFieldStore);
	const myStateRef = useRef({});
	const isDataLoading = useRef(false);
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const setIsDataLoading = (data) => {
		isDataLoading.current = data;
	};

	const generateParams = (pageNumber) => {
		let params = generateFilterParams(filterData, 'COLLECTION_LIST');
		params = params.replace('status=SUGGESTED', `approvalStatus=${activeTab}`);
		return `?${params}page=${pageNumber}&size=20&collectionViewType=AUTO_GENERATE`;
	};

	const fetchSuggestedCollections = async (pageNumber) => {
		try {
			dispatch(openLoader());
			const { data } = await ApiClient.GET('getCollectionList', generateParams(pageNumber));
			if (pageNumber === 0) {
				myStateRef.current = data;
				setCollections(data.data);
				setTotalCount(data.data.length);
			} else {
				let modifiedData = [...myStateRef.current.data, ...data.data];
				myStateRef.current = { ...data, data: modifiedData };
				setCollections(modifiedData);
				setTotalCount(modifiedData.length);
			}
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		fetchSuggestedCollections(0);
		document.addEventListener('scroll', handleScroll);
		return () => {
			document.removeEventListener('scroll', handleScroll);
		};
	}, [activeTab, filterData]);

	const handleScroll = async () => {
		if (isPageReachBottom()) {
			let { totalElements, currentPage, data } = myStateRef.current;
			if (totalElements > 0) {
				if (totalElements !== data.length && !isDataLoading.current) {
					setIsDataLoading(true);
					await fetchSuggestedCollections(currentPage + 1);
					setIsDataLoading(false);
				}
			}
		}
	};

	const onCollectionFavourite = async (id) => {
		try {
			dispatch(openLoader());
			const { data } = await ApiClient.POST('collectionAction', {}, `${id}/like`);
			toastSuccess(data.message);
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const togglePin = (id) => {
		return collections?.map((item) => {
			if (item.id === id) {
				item.isPinned = !item.isPinned;
			}
			return item;
		});
	};

	const onPinClick = async (id) => {
		try {
			dispatch(openLoader());
			const { data } = await ApiClient.POST('pinCollection', {}, `${id}/pin`);
			let modifiedData = togglePin(id);
			const selected = modifiedData.find((item) => item.id === id);
			modifiedData = modifiedData.filter((item) => item.id !== id);
			modifiedData.unshift(selected);
			myStateRef.current = { ...myStateRef.current, data: modifiedData };
			setCollections(modifiedData);
			toastSuccess(data.message);
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const onUnPinClick = async (id) => {
		try {
			dispatch(openLoader());
			const { data } = await ApiClient.POST('pinCollection', {}, `${id}/unpin`);
			const modifiedData = togglePin(id);
			myStateRef.current = { ...myStateRef.current, data: modifiedData };
			setCollections(modifiedData);
			toastSuccess(data.message);
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const handlePageRoute = (id) => {
		navigate(`/collections/view/${id}?status=SUGGESTED_FOR_BUYER`);
	};

	const renderImages = (documents = []) => {
		return documents.slice(0, 4)?.map((item, i) => {
			return (
				<div className="single-item" key={`collection_img_${i}`}>
					<img src={item} alt="style" />
				</div>
			);
		});
	};

	const renderCollections = () => {
		return collections?.map((collection) => {
			return (
				<div
					className="single-collection-card d-flex align-items-center"
					key={`suggested_collection_${collection.id}`}
				>
					<span className={`pin ${collection?.isPinned ? 'pinned' : ''} cursor-pointer`}>
						{collection?.isPinned && (
							<img
								onClick={() => onUnPinClick(collection.id)}
								src={`${process.env.PUBLIC_URL}/icons/pinned.svg`}
								alt="pin"
							/>
						)}
						{!collection?.isPinned && (
							<img
								onClick={() => onPinClick(collection.id)}
								src={`${process.env.PUBLIC_URL}/icons/pin.svg`}
								alt="pin"
							/>
						)}
					</span>
					<div className="option">
						<div className="dropdown show">
							<button
								className="btn btn-default dropdown-toggle"
								type="button"
								id="menu1"
								data-toggle="dropdown"
								aria-expanded="true"
							>
								<img src={`${process.env.PUBLIC_URL}/icons/more_icon.svg`} alt="more" />
							</button>
							<ul className="dropdown-menu dropdown-menu-right shadow-lg">
								<li
									className="px-4 pb-3"
									onClick={async (e) => {
										e.stopPropagation();
										await onCollectionFavourite(collection.id);
									}}
								>
									<span>Add to favourite</span>
								</li>
							</ul>
						</div>
					</div>
					<div className="info cursor-pointer" onClick={() => handlePageRoute(collection.id)}>
						<span className="brand-common-design mb-1">{collection?.brand}</span>
						<CollectionTags tags={collection.tagResponseList} isShowNew={collection?.isNew} />
						{/* {collection?.tagCategory === 'BUYER_SELECTION' && (
							<div className="collection-status pt-2 pb-2">
								<span className="status pink-1 buyers-selection">Buyer selection</span>
							</div>
						)} */}
						<h2 className="regular-16 weight-500 my-12">
							{collection?.collectionName} ({collection?.numOfDesign})
						</h2>
						<ul className="common-list-component">
							<li className="regular-14">{collection?.market}</li>
							{collection?.season && (
								<li className="regular-14">{collection?.season?.replace('_', ' ')}</li>
							)}
						</ul>
					</div>
					<div
						className="four-item-image cursor-pointer"
						onClick={() => handlePageRoute(collection.id)}
					>
						{renderImages(collection.documentPathList)}
					</div>
				</div>
			);
		});
	};

	return (
		<>
			<div className="inner-tabs mb-12">
				<ul>
					<li
						className={activeTab === 'PENDING' ? 'active' : ''}
						onClick={() => setActiveTab('PENDING')}
					>
						Pending
					</li>
					<li
						className={activeTab === 'APPROVED' ? 'active' : ''}
						onClick={() => setActiveTab('APPROVED')}
					>
						Approved
					</li>
				</ul>
			</div>

			<div className="brand-wise-collections">
				<div className="single-brand-collections mb-5">
					<div className="collections-cards">{renderCollections()}</div>
				</div>
			</div>
		</>
	);
};

export default SuggestedCollection;
